<template>
  <div v-if="visible" class="dialog-overlay" role="dialog" aria-modal="true" @keydown.esc.prevent.stop="onClose">
    <div class="dialog">
      <header class="dialog-header">
        <h2 class="h2">Procurat-Abgleich</h2>
        <button class="btn" @click="onClose">Schließen</button>
      </header>

      <section class="dialog-body">
        <div class="actions">
          <label class="btn">
            <input type="file" accept=".csv,text/csv" @change="onPickFile" hidden />
            CSV auswählen
          </label>
          <button class="btn btn-primary" :disabled="!file || loading" @click="onUpload">
            <span v-if="loading" class="spinner" aria-hidden="true"></span>
            Hochladen & Vorschau
          </button>
        </div>

        <div v-if="error" class="notice notice-danger mt-12" role="alert">{{ error }}</div>

        <div v-if="preview">
          <div class="summary mt-12" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
            <button
              type="button"
              class="badge clickable"
              :class="{'active': isActiveFilter('NEW')}"
              @click="toggleFilter('NEW')"
              aria-pressed="isActiveFilter('NEW') ? 'true' : 'false'"
            >
              Neu: {{ preview.stats.new }}
            </button>
            <button
              type="button"
              class="badge clickable"
              :class="{'active': isActiveFilter('UPDATE')}"
              @click="toggleFilter('UPDATE')"
              aria-pressed="isActiveFilter('UPDATE') ? 'true' : 'false'"
            >
              Updates: {{ preview.stats.updates }}
            </button>
            <button
              type="button"
              class="badge clickable"
              :class="{'active': isActiveFilter('MISSING_INACTIVE')}"
              @click="toggleFilter('MISSING_INACTIVE')"
              aria-pressed="isActiveFilter('MISSING_INACTIVE') ? 'true' : 'false'"
            >
              Fehlt→inaktiv: {{ preview.stats.missing_to_inactive }}
            </button>
            <button
              type="button"
              class="badge clickable"
              :class="{'active': isActiveFilter('MISSING_GRADUATED')}"
              @click="toggleFilter('MISSING_GRADUATED')"
              aria-pressed="isActiveFilter('MISSING_GRADUATED') ? 'true' : 'false'"
            >
              Fehlt→ausgeschult: {{ preview.stats.missing_to_graduated }}
            </button>
            <button
              type="button"
              class="badge clickable danger"
              :class="{'active': isActiveFilter('BLOCKER')}"
              @click="toggleFilter('BLOCKER')"
              aria-pressed="isActiveFilter('BLOCKER') ? 'true' : 'false'"
            >
              Blocker: {{ preview.stats.blockers }}
            </button>

            <button
              type="button"
              class="btn"
              style="margin-left:auto"
              :disabled="activeFilters.length === 0"
              @click="clearFilters"
            >
              Alle
            </button>
          </div>

          <div class="table mt-12">
            <div class="thead">
              <div class="th th-check"></div>
              <div class="th">Aktion</div>
              <div class="th">Schüler</div>
              <div class="th">Eltern (Bestand)</div>
              <div class="th">Eltern (Procurat)</div>
              <div class="th">Hinweise</div>
            </div>
            <div class="tbody">
              <div v-for="(row) in rows" :key="row.key"
                   class="tr"
                   :class="rowClass(row)">
                <div class="td th-check">
                  <input type="checkbox"
                         :disabled="row.type === 'BLOCKER'"
                         v-model="selected[row.key]" />
                </div>
                <div class="td">
                  <span class="badge" :class="badgeClass(row)">{{ labelFor(row) }}</span>
                </div>
                <div class="td">
                  <div class="muted">Bestand:
                    <template v-if="row.student.db && row.student.db.id">
                      {{ row.student.db.last_name }}, {{ row.student.db.first_name }}
                      · Geb {{ formatDateDe(row.student.db.birth_date) || '–' }}
                      · Kl {{ row.student.db.class_level ?? '–' }}
                    </template>
                    <template v-else>–</template>
                  </div>
                  <div>Procurat:
                    <template v-if="row.student.csv">
                      {{ row.student.csv.last_name }}, {{ row.student.csv.first_name }}
                      · Geb {{ formatDateDe(row.student.csv.birth_date) || '–' }}
                      · Kl {{ row.student.csv.class_level ?? '–' }}
                    </template>
                    <template v-else>–</template>
                  </div>
                </div>
                <div class="td">
                  <template v-if="row.parents && row.parents.db && row.parents.db.length">
                    <div v-for="p in row.parents.db" :key="p.email">
                      <strong>{{ formatName(p) }}</strong>
                      <span class="muted"> &lt;{{ p.email }}&gt;</span>
                      <span class="muted"> (Rel {{ p.relationship_id ?? '–' }})</span>
                    </div>
                  </template>
                  <template v-else>–</template>
                </div>
                <div class="td">
                  <template v-if="row.parents && row.parents.csv && row.parents.csv.length">
                    <div v-for="p in row.parents.csv" :key="p.email">
                      <strong>{{ formatName(p) }}</strong>
                      <span class="muted"> &lt;{{ p.email }}&gt;</span>
                      <span class="muted"> (Rel {{ p.relationship_id ?? '–' }})</span>
                    </div>
                  </template>
                  <template v-else>–</template>
                </div>
                <div class="td">
                  <div v-if="row.blockers && row.blockers.length" class="muted danger">
                    <div v-for="(b,i) in row.blockers" :key="i">BLOCKER: {{ b }}</div>
                  </div>
                  <div v-else>
                    <template v-if="row.parents && row.parents.ops">
                      <span
                        v-if="row.parents.ops.add && row.parents.ops.add.length"
                        class="badge"
                        :title="`Eltern-Link(s) wird/werden hinzugefügt: ${row.parents.ops.add.length}`"
                      >+{{ row.parents.ops.add.length }}</span>
                      <span
                        v-if="row.parents.ops.role_change && row.parents.ops.role_change.length"
                        class="badge"
                        :title="`Eltern-Rolle(n) wird/werden geändert: ${row.parents.ops.role_change.length}`"
                      >~{{ row.parents.ops.role_change.length }}</span>
                      <span
                        v-if="row.parents.ops.remove && row.parents.ops.remove.length"
                        class="badge"
                        :title="`Eltern-Link(s) wird/werden entfernt: ${row.parents.ops.remove.length}`"
                      >-{{ row.parents.ops.remove.length }}</span>
                    </template>

                    <span
                      v-if="row.parents && row.parents.deactivate_candidates && row.parents.deactivate_candidates.length"
                      class="badge danger"
                      :title="deactivateCandidatesTitle(row.parents.deactivate_candidates)"
                    >
                      Eltern inaktiv: {{ row.parents.deactivate_candidates.length }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="actions mt-12">
            <button class="btn btn-primary" :disabled="submitting || nothingSelected" @click="onApply">
              <span v-if="submitting" class="spinner" aria-hidden="true"></span>
              Verarbeiten
            </button>
          </div>

          <div v-if="applyResult" class="notice mt-12" role="status" style="padding:12px;">
            <p class="muted" style="margin:0 0 8px 0;">
              Verarbeitung abgeschlossen –
              Neu: {{ applyResult.created ?? 0 }},
              Updates: {{ applyResult.updated ?? 0 }},
              Inaktiv: {{ applyResult.inactivated ?? 0 }},
              Ausgeschult: {{ applyResult.graduated ?? 0 }},
              Eltern deaktiviert: {{ applyResult.parents_deactivated ?? 0 }}.
            </p>
            <div v-if="Array.isArray(applyResult.deactivated_parents) && applyResult.deactivated_parents.length">
              <strong>Deaktivierte Eltern:</strong>
              <ul style="margin:6px 0 0 16px; padding:0;">
                <li v-for="p in applyResult.deactivated_parents" :key="p.id">
                  <template v-if="hasName(p)">
                    {{ formatName(p) }} <span class="muted"> &lt;{{ p.email }}&gt;</span>
                  </template>
                  <template v-else>
                    <span class="muted">{{ p.email }}</span>
                  </template>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { uploadProcuratCsv, applyProcurat } from '@/services/procurat';

export default {
  name: 'ProcuratDialog',
  props: {
    visible: { type: Boolean, default: false },
  },
  emits: ['close', 'applied'],
  data() {
    return {
      file: null,
      loading: false,
      submitting: false,
      error: '',
      preview: null, // {fileId, stats, rows}
      selected: {},
      activeFilters: [], // z. B. ['NEW','UPDATE',...]
      applyResult: null, // {created, updated, ..., deactivated_parents:[...]}
    };
  },
  computed: {
    rows() {
      if (!this.preview) return [];
      const list = Array.isArray(this.preview.rows) ? this.preview.rows : [];
      if (!this.activeFilters.length) return list;
      const set = new Set(this.activeFilters);
      return list.filter(r => set.has(String(r.type)));
    },
    nothingSelected() {
      return !Object.values(this.selected).some(Boolean);
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
      this.reset();
    },
    toggleFilter(type) {
      const t = String(type);
      if (this.activeFilters.includes(t)) {
        this.activeFilters = this.activeFilters.filter(x => x !== t);
      } else {
        this.activeFilters = [...this.activeFilters, t];
      }
    },
    isActiveFilter(type) {
      return this.activeFilters.includes(String(type));
    },
    clearFilters() {
      this.activeFilters = [];
    },
    formatName(p) {
      const ln = (p && p.last_name) ? String(p.last_name).trim() : '';
      const fn = (p && p.first_name) ? String(p.first_name).trim() : '';
      const name = [ln, fn].filter(Boolean).join(', ');
      return name || (p?.email ?? '');
    },
    hasName(p) {
      const ln = (p && p.last_name) ? String(p.last_name).trim() : '';
      const fn = (p && p.first_name) ? String(p.first_name).trim() : '';
      return !!(ln || fn);
    },
    deactivateCandidatesTitle(list) {
      try {
        return list
          .map(p => {
            const name = this.hasName(p) ? this.formatName(p) : '';
            const email = p?.email ? String(p.email) : '';
            return name ? `${name} <${email}>` : email;
          })
          .filter(Boolean)
          .join('\n');
      } catch {
        return '';
      }
    },
    formatDateDe(iso) {
      if (!iso || typeof iso !== 'string') return '';
      // Erwartet 'YYYY-MM-DD'
      const parts = iso.split('-');
      if (parts.length !== 3) return '';
      const [y, m, d] = parts;
      if (!y || !m || !d) return '';
      const dd = String(d).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const yyyy = String(y).padStart(4, '0');
      return `${dd}.${mm}.${yyyy}`;
    },
    onPickFile(e) {
      const f = e.target.files?.[0];
      if (f) {
        this.file = f;
        this.error = '';
      }
    },
    async onUpload() {
      if (!this.file) return;
      this.loading = true;
      this.error = '';
      this.preview = null;
      try {
        const res = await uploadProcuratCsv(this.file);
        this.preview = res;
        // Default: alle Zeilen ausgewählt außer BLOCKER
        const sel = {};
        if (res && Array.isArray(res.rows)) {
          for (const r of res.rows) {
            sel[r.key] = r.type !== 'BLOCKER';
          }
        }
        this.selected = sel;
        this.activeFilters = []; // Filter zurücksetzen
      } catch (e) {
        this.error = e?.response?.data?.error?.message || e.message || 'Upload fehlgeschlagen';
      } finally {
        this.loading = false;
      }
    },
    async onApply() {
      if (!this.preview) return;
      const keys = Object.entries(this.selected).filter(([, v]) => !!v).map(([k]) => k);
      if (keys.length === 0) return;
      this.submitting = true;
      this.error = '';
      try {
        const res = await applyProcurat(this.preview.fileId, keys);
        this.applyResult = res || {};
        this.$emit('applied', res); // Parent kann Liste neu laden
        // Dialog absichtlich NICHT resetten, damit Ergebnis sichtbar bleibt
      } catch (e) {
        this.error = e?.response?.data?.error?.message || e.message || 'Verarbeitung fehlgeschlagen';
      } finally {
        this.submitting = false;
      }
    },
    labelFor(row) {
      switch (row.type) {
        case 'NEW': return 'NEU';
        case 'UPDATE': return 'UPDATE';
        case 'MISSING_INACTIVE': return 'INAKTIV';
        case 'MISSING_GRADUATED': return 'AUSGESCHULT';
        case 'BLOCKER': return 'BLOCKER';
        default: return 'OK';
      }
    },
    badgeClass(row) {
      switch (row.type) {
        case 'NEW': return 'success';
        case 'UPDATE': return 'warn';
        case 'MISSING_INACTIVE':
        case 'MISSING_GRADUATED':
        case 'BLOCKER':
          return 'danger';
        default: return '';
      }
    },
    rowClass(row) {
      return {
        'bg-success': row.type === 'NEW',
        'bg-warn': row.type === 'UPDATE',
        'bg-danger': row.type === 'MISSING_INACTIVE' || row.type === 'MISSING_GRADUATED',
      };
    },
    reset() {
      this.file = null;
      this.preview = null;
      this.selected = {};
      this.error = '';
      this.loading = false;
      this.submitting = false;
    },
  },
};
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
}
.dialog {
  width: 94vw;
  max-width: 94vw;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #E0E3EA);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,.12);
  overflow: hidden;
}
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border, #E0E3EA);
}
.dialog-body {
  padding: 12px 16px 16px 16px;
}
.actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.table {
  border: 1px solid var(--border, #E0E3EA);
  border-radius: 8px;
  overflow: hidden;
}
.thead, .tr {
  display: grid;
  grid-template-columns: 32px 130px 1fr 1fr 1fr 200px;
  gap: 8px;
}
.thead {
  align-items: center;
}
.tr {
  align-items: stretch; /* alle Zellen gleiche Höhe wie höchste Zelle */
  min-height: 48px; /* einheitliche Mindesthöhe je Datenzeile */
}
.tbody {
  max-height: 60vh;
  overflow-y: auto;
}
.th, .td {
  padding: 8px;
  border-bottom: 1px solid var(--border, #E0E3EA);
  font-size: 14px;
  line-height: 1.4;
}
/* Zellen-Inhalt top-align und volle Zellenhöhe nutzen */
.td {
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* oben ausrichten */
  height: 100%;
}
.th-check {
  display: flex;
  align-items: center;
  justify-content: center;
}
.thead .th {
  font-size: 12px;
  color: var(--muted, #6B7280);
  font-weight: 500;
  background: var(--bg, #F6F7F9);
}
.tr:last-child .td { border-bottom: none; }
.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: #E5E7EB;
}
.badge.success { background: rgba(16,185,129,.15); color: #065F46; }
.badge.warn { background: rgba(245,158,11,.14); color: #7C2D12; }
.badge.danger { background: rgba(239,68,68,.14); color: #7F1D1D; }
.badge.clickable {
  cursor: pointer;
  border: 1px solid transparent;
}
.badge.clickable.active {
  border-color: var(--primary, #4F46E5);
  box-shadow: 0 0 0 2px var(--focus, rgba(79,70,229,.45));
}
.bg-success { background: rgba(16,185,129,.08); }
.bg-warn { background: rgba(245,158,11,.15); }
.bg-danger { background: rgba(239,68,68,.08); }
.muted { color: var(--muted, #6B7280); font-size: 12px; }
.muted.danger { color: #B91C1C; }
.btn {
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border, #E0E3EA);
  background: var(--surface, #fff);
  cursor: pointer;
}
.btn-primary {
  background: var(--primary, #4F46E5);
  color: #fff;
  border-color: var(--primary, #4F46E5);
}
.btn-primary:hover { background: var(--primary-600, #4338CA); }
.spinner {
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.5);
  border-top-color: #fff;
  display: inline-block; margin-right: 6px;
  animation: spin 1s linear infinite;
  vertical-align: -3px;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
